import type { IViewPortOrientation } from '@/engine/app/interfaces';
import { ViewPortOrientation } from '@/engine/app/models/ViewPortOrientation';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { ViewsService } from '@/engine/app/Views.service';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';

export class DefaultOrientationProvider {
  private _planningAlgorithm?: PlanningAlgorithm;

  private _viewsService?: ViewsService;

  public setPlanningAlgorithm(planningAlgorithm: PlanningAlgorithm): void {
    this._planningAlgorithm = planningAlgorithm;
  }

  public setViewService(viewsService: ViewsService): void {
    this._viewsService = viewsService;
  }

  public trackModelFrontOrientationChanges(callback: (orientation: IViewPortOrientation) => void): CallbackSubscription {
    if (!this._planningAlgorithm) {
      throw new Error('Algorithm is not defined');
    }
    if (!this._viewsService?.currentView) {
      throw new Error('ViewsService is not defined');
    }
    const viewOrientationDescriptor = this._viewsService.currentView.getViewOrientationDescriptor();
    return this._planningAlgorithm.addOnChangedCallbackFor<DT.Orientation>(
      viewOrientationDescriptor,
      (orientation: DT.Orientation | undefined): void => {
        if (orientation) {
          callback(new ViewPortOrientation(orientation.value.top, orientation.value.dir));
        }
      }
    );
  }

  public async getModelFrontOrientation(): Promise<IViewPortOrientation> {
    if (!this._planningAlgorithm) {
      throw new Error('Algorithm is not defined');
    }
    if (!this._viewsService?.currentView) {
      throw new Error('ViewsService is not defined');
    }
    const viewOrientationDescriptor = this._viewsService.currentView.getViewOrientationDescriptor();
    const orientation = await this._planningAlgorithm.getValueAsync(viewOrientationDescriptor, DT.Orientation);
    if (!orientation) {
      throw new Error('Orientation is not initialized');
    }
    return new ViewPortOrientation(orientation.value.top, orientation.value.dir);
  }
}
